import { v3 } from "oc-threejs/short3";
import * as Scenes from "oc-threejs/Scenes";
import { Color, Light, PerspectiveCamera, Scene, WebGLRenderer } from "three";

interface IApp {
  scene: Scene;
  renderer: WebGLRenderer;
  camera: PerspectiveCamera;
  [key: string]: any;
}

declare const APP: IApp;

export class SnapshotRenderer {
  static cachedRenderer: WebGLRenderer | undefined;

  renderer!: WebGLRenderer;
  scene!: Scene;
  camera!: PerspectiveCamera;

  constructor() {
    this.initRenderer();
    this.initScene();
    this.initCamera();
    this.initEnvironment();
    this.initLights();
  }

  initScene(): void {
    this.scene = new Scene();
    this.scene.name = "SnapshotRenderer";
  }

  initLights(): void {
    // strict type for x
    const lights = (Scenes as any)
      .filter(APP.scene, (x: any) => x instanceof Light)
      .map((x: any) => x.clone());
    lights.forEach((x: Light) => this.scene.add(x));
  }

  initEnvironment(): void {
    this.scene.environment = APP.scene.environment;
  }

  initRenderer(): void {
    if (SnapshotRenderer.cachedRenderer) {
      this.renderer = SnapshotRenderer.cachedRenderer;
    } else {
      SnapshotRenderer.cachedRenderer = this.renderer = new WebGLRenderer({
        antialias: true,
        preserveDrawingBuffer: true,
        alpha: true,
        logarithmicDepthBuffer: true,
      });
      const keys = ["toneMapping", "toneMappingExposure", "outputColorSpace"] as const;
      keys.forEach((key) => {
        // APP.renderer might be a specific ThreeJS renderer type, accessing props dynamically needs index signature or casting
        (this.renderer as any)[key] = APP.renderer[key];
      });
      this.renderer.setClearColor(new Color(0xffffff), 0);
    }
  }

  initCamera(): void {
    this.camera = new PerspectiveCamera(APP.camera.fov, 1, 0.1, 100);
    this.camera.position.copy(v3(0, 3, 0));
    this.scene.add(this.camera);
  }

  setSize(width: number, height: number): void {
    if (width < 1 || height < 1) {
      throw new Error("width or height < 1");
    }
    this.camera.aspect = width / height;
    this.renderer.setSize(width, height);
  }

  getImage(
    scene: Scene = this.scene,
    camera: PerspectiveCamera = this.camera,
    mimetype: string = "image/png",
  ): string {
    this.render(scene, camera);
    return this.renderer.domElement.toDataURL(mimetype);
  }

  getBlobPromise(mimetype: string = "image/jpg"): Promise<Blob | null> {
    this.render();
    return new Promise((resolve) => {
      this.renderer.domElement.toBlob(resolve, mimetype);
    });
  }

  render(scene: Scene = this.scene, camera: PerspectiveCamera = this.camera): void {
    camera.updateProjectionMatrix();
    this.renderer.render(scene, camera);
  }

  getAsBackgroundImage(
    element: HTMLElement,
    scene: Scene = this.scene,
    camera: PerspectiveCamera = this.camera,
  ): string {
    const rect = element.getBoundingClientRect();
    this.setSize(rect.width, rect.height);
    return this.getImage(scene, camera);
  }

  getAsBackgroundImageForSize(
    width: number,
    height: number,
    scene: Scene = this.scene,
    camera: PerspectiveCamera = this.camera,
  ): string {
    this.setSize(width, height);
    return this.getImage(scene, camera);
  }

  setAsBackgroundImage(element: HTMLElement): void {
    const imageUrl = this.getAsBackgroundImage(element);
    const style = element.style;
    style.backgroundImage = `url(${imageUrl})`;
    style.backgroundPosition = `center`;
    style.backgroundSize = `contain`;
    style.backgroundRepeat = `no-repeat`;
  }

  dispose(): void {
    (this.renderer as any) = null;
    (this.camera as any) = null;
    (this.scene as any) = null;
  }
}
